import { useState } from "react";
import { OutlineButton } from "@/components/common/outline-button";
import { TextButton } from "@/components/common/text-button";
import { AudioPlayButton } from "@/components/common/audio-play-button";
import { Spinner } from "@/components/ui/modal";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { useUpload } from "@/hooks/use-upload";
import { cn } from "@/utils/cn";
import { AUDIO_ALLOWED_TYPES, AUDIO_MAX_FILE_SIZE } from "./constants";
import { useTranslation } from "react-i18next";

export interface SaveStepActions {
  setSaving: (flag: boolean) => void;
}

interface SaveStepProps {
  className?: string;
  progress: number;
  // 根据 type 展示不同的文案和交互
  type: "create" | "clone";
  name: string;
  URL: string;
  onUpload: (file: File | null) => void;
  onInvalid: () => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
}

export const SaveStep = ({
  className,
  progress,
  type,
  name,
  URL,
  onUpload,
  onInvalid,
  onSave,
  onCancel,
}: SaveStepProps) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const { triggerUpload } = useUpload({
    acceptedFileTypes: AUDIO_ALLOWED_TYPES.join(','),
    maxFileSize: AUDIO_MAX_FILE_SIZE,
    onUpload: (file) => {
      onUpload(file);
    },
    onInvalid: () => {
      onInvalid();
    },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-14 py-6",
        className
      )}
    >
      {/* 加载状态 */}
      {progress < 100 && (
        <div className="mb-7 flex flex-col items-center gap-2.5">
          {/* 加载图标 */}
          <Spinner className="w-8 h-8" />

          {/* 加载文本 */}
          <div className="flex flex-col items-center gap-1">
            <h3 className="text-base text-[rgba(16,24,40,1] font-medium">
              {t('custom_digital_human.create_form.generate_audio')}
            </h3>
            <p className="text-sm text-[rgba(102,112,133,1)]">
              {t('custom_digital_human.create_form.please_wait')}
            </p>
          </div>
        </div>
      )}

      <div
        className={cn(
          "mb-8 flex items-start gap-3 p-5 w-full h-20 rounded-xl border border-[rgba(234,236,240,1)]",
          {
            "mb-36": progress === 100,
          }
        )}
      >
        <AudioPlayButton
          url={URL}
          className="w-9 h-9"
          iconClassName="w-9 h-9"
          disabled={progress < 100}
        />
        <div className="flex-1">
          <h4 className="text-base font-medium text-[rgba(52,64,84,1)]">
            {name}
          </h4>
          <p className="text-sm text-[rgba(102,112,133,1)]">
            {t('custom_digital_human.create_form.my_voice')}
          </p>
        </div>
        {/* 进度指示器 */}
        {progress < 100 && (
          <div className="w-8 h-8 relative">
            <svg className="w-8 h-8 transform -rotate-90" viewBox="0 0 32 32">
              <circle
                cx="16"
                cy="16"
                r="14"
                stroke="rgba(242,244,247,1)"
                strokeWidth="4"
                fill="none"
              />
              {progress > 0 && (
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="rgba(117,66,247,1)"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 14}`}
                  strokeDashoffset={`${2 * Math.PI * 14 * (1 - (progress || 100) / 100)
                    }`}
                />
              )}
            </svg>
          </div>
        )}
      </div>

      {/* 音频生成完毕，会自动保存 */}
      {/* 按钮区域 */}
      {/* <div className="flex justify-center items-center gap-3.5 w-full">
        {type === "create" && (
          <ConfirmModal
            trigger={<OutlineButton className="w-64" label={t('custom_digital_human.create_form.reupload_voice')} disabled={progress < 100} />}
            title={t('custom_digital_human.create_form.reupload_voice')}
            description={t('custom_digital_human.create_form.reupload_voice_tips')}
            onConfirm={triggerUpload}
          />
        )}
        {type === "clone" && (
          <ConfirmModal
            trigger={<OutlineButton className="w-64" label="Cancel" disabled={progress < 100} />}
            title={t('custom_digital_human.create_form.cancel_clone')}
            description={t('custom_digital_human.create_form.cancel_clone_tips')}
            onConfirm={onCancel}
          />
        )}
        <TextButton
          className="w-64"
          label={saving ? <Spinner className="w-5 h-5" /> : t('common.save')}
          disabled={progress < 100}
          onClick={() => saving || handleSave()}
        />
      </div> */}
    </div>
  );
};

SaveStep.displayName = "SaveStep";